import { formatNumericDate } from '@escala/domain/dates';
import {
  type CalendarDocument,
  type CalendarExportDay,
  type CalendarExportEvent,
  calendarFileName,
} from '@escala/domain/shareCalendar';

import { loadFonts } from './assets';
import {
  COLORS,
  type Ctx,
  canvasBlob,
  createCanvas,
  ellipsize,
  font,
  headerColor,
  mix,
  roundRect,
  wrapLines,
} from './canvas';
import type { ShareFile, ShareOptions } from './image';
import { A4_LANDSCAPE, buildPdf } from './pdfWriter';

/**
 * O calendário exportado (modo Calendário do "Exportar eventos"): a grade do mês ou da
 * semana com o título inteiro de cada evento, sem escala. Larga (1600 unidades), para os
 * sete dias caberem lado a lado com o nome legível; cada semana cresce até caber o dia
 * mais cheio. O PDF é uma página A4 deitada com a mesma grade, reduzida para caber.
 */

const WIDTH = 1600;
const MARGIN = 48;
const HEADER = 196;
const WEEKDAYS = 48;
const FOOTER = 64;
const CELL_PAD = 10;
const DAY_ROW = 34;
const PILL_GAP = 6;
const PILL_PAD = 8;
const TIME_SIZE = 15;
const TITLE_SIZE = 17;
const TITLE_LINE = 21;

interface Layout {
  cell: number;
  /** Altura de cada semana. */
  rows: number[];
  /** Linhas do título de cada evento, já quebradas. */
  lines: Map<string, string[]>;
  height: number;
}

function pillHeight(lines: readonly string[]): number {
  return PILL_PAD * 2 + 18 + lines.length * TITLE_LINE;
}

function measure(ctx: Ctx, doc: CalendarDocument): Layout {
  const cell = (WIDTH - MARGIN * 2) / 7;
  // Na semana sobra altura: o título pode ocupar mais linhas.
  const maxLines = doc.kind === 'week' ? 5 : 3;
  const minRow = doc.kind === 'week' ? 360 : 132;
  const lines = new Map<string, string[]>();

  ctx.font = font(700, TITLE_SIZE);

  const rows = doc.weeks.map((week) => {
    const tallest = Math.max(
      0,
      ...week.map((day) =>
        day.events.reduce((sum, e, i) => {
          const wrapped = wrapLines(ctx, e.title, cell - CELL_PAD * 2 - PILL_PAD * 2 - 4, maxLines);

          lines.set(`${day.date}|${e.id}`, wrapped);

          return sum + pillHeight(wrapped) + (i ? PILL_GAP : 0);
        }, 0)
      )
    );

    return Math.max(minRow, DAY_ROW + tallest + CELL_PAD * 2);
  });

  const grid = rows.reduce((a, b) => a + b, 0);

  return { cell, rows, lines, height: HEADER + 32 + WEEKDAYS + grid + 24 + FOOTER };
}

function drawHeader(ctx: Ctx, doc: CalendarDocument, org: string | null, color: string): void {
  const base = headerColor(color);
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEADER);

  gradient.addColorStop(0, mix(base, '#000000', 0.05));
  gradient.addColorStop(1, mix(base, '#0F172A', 0.45));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, HEADER);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.07)';
  ctx.beginPath();
  ctx.arc(WIDTH - 80, 20, 210, 0, Math.PI * 2);
  ctx.fill();

  let y = 48;

  if (org) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
    ctx.font = font(700, 20);
    ctx.fillText(ellipsize(ctx, org.toUpperCase(), WIDTH - MARGIN * 2), MARGIN, y + 20);
    y += 38;
  }

  ctx.fillStyle = COLORS.white;
  ctx.font = font(800, 46);
  ctx.fillText(ellipsize(ctx, doc.title, WIDTH - MARGIN * 2), MARGIN, y + 44);
  y += 58;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.86)';
  ctx.font = font(500, 26);
  ctx.fillText(ellipsize(ctx, doc.subtitle, WIDTH - MARGIN * 2), MARGIN, y + 26);
}

function drawEvent(
  ctx: Ctx,
  event: CalendarExportEvent,
  lines: readonly string[],
  x: number,
  y: number,
  w: number
) {
  const h = pillHeight(lines);

  roundRect(ctx, x, y, w, h, 8);
  ctx.fillStyle = event.tint;
  ctx.fill();
  ctx.fillStyle = event.color;
  roundRect(ctx, x, y, 4, h, 2);
  ctx.fill();

  ctx.fillStyle = mix(event.color, '#0F172A', 0.35);
  ctx.font = font(700, TIME_SIZE);
  ctx.fillText(event.time, x + PILL_PAD + 4, y + PILL_PAD + 14);

  ctx.fillStyle = COLORS.ink;
  ctx.font = font(700, TITLE_SIZE);
  lines.forEach((line, i) => ctx.fillText(line, x + PILL_PAD + 4, y + PILL_PAD + 18 + 16 + i * TITLE_LINE));
}

function drawDay(ctx: Ctx, day: CalendarExportDay, layout: Layout, x: number, y: number, h: number) {
  const w = layout.cell;

  ctx.fillStyle = day.outside ? COLORS.page : COLORS.card;
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = COLORS.cardLine;
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w, h);

  ctx.fillStyle = day.outside ? COLORS.faint : COLORS.body;
  ctx.font = font(day.outside ? 500 : 700, 20);
  ctx.textAlign = 'right';
  ctx.fillText(String(day.day), x + w - CELL_PAD - 2, y + CELL_PAD + 20);
  ctx.textAlign = 'left';

  let top = y + CELL_PAD + DAY_ROW;

  for (const event of day.events) {
    const lines = layout.lines.get(`${day.date}|${event.id}`) ?? [event.title];

    drawEvent(ctx, event, lines, x + CELL_PAD, top, w - CELL_PAD * 2);
    top += pillHeight(lines) + PILL_GAP;
  }
}

function draw(doc: CalendarDocument, options: ShareOptions): HTMLCanvasElement {
  const { ctx: probe } = createCanvas(1, 1, 1);
  const layout = measure(probe, doc);
  const scale = Math.min(2, Math.sqrt(16_000_000 / (WIDTH * layout.height)));
  const { canvas, ctx } = createCanvas(WIDTH, layout.height, scale);

  ctx.fillStyle = COLORS.page;
  ctx.fillRect(0, 0, WIDTH, layout.height);
  drawHeader(ctx, doc, options.org, options.color);

  const gridTop = HEADER + 32;
  const gridHeight = WEEKDAYS + layout.rows.reduce((a, b) => a + b, 0);

  // A grade num cartão branco com cantos arredondados.
  ctx.save();
  roundRect(ctx, MARGIN, gridTop, WIDTH - MARGIN * 2, gridHeight, 18);
  ctx.clip();
  ctx.fillStyle = COLORS.card;
  ctx.fillRect(MARGIN, gridTop, WIDTH - MARGIN * 2, gridHeight);

  ctx.fillStyle = COLORS.muted;
  ctx.font = font(700, 18);
  ctx.textAlign = 'center';
  doc.weekdays.forEach((label, i) =>
    ctx.fillText(label, MARGIN + layout.cell * i + layout.cell / 2, gridTop + WEEKDAYS / 2 + 6)
  );
  ctx.textAlign = 'left';

  let y = gridTop + WEEKDAYS;

  doc.weeks.forEach((week, w) => {
    const h = layout.rows[w]!;

    week.forEach((day, i) => drawDay(ctx, day, layout, MARGIN + layout.cell * i, y, h));
    y += h;
  });
  ctx.restore();

  roundRect(ctx, MARGIN, gridTop, WIDTH - MARGIN * 2, gridHeight, 18);
  ctx.strokeStyle = COLORS.cardLine;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.fillStyle = COLORS.faint;
  ctx.font = font(500, 18);
  ctx.textAlign = 'center';
  ctx.fillText(
    `Gerado pelo Live Gospel em ${formatNumericDate(options.generatedAt)}`,
    WIDTH / 2,
    layout.height - FOOTER + 36
  );
  ctx.textAlign = 'left';

  return canvas;
}

/** O calendário como imagem PNG. */
export async function renderCalendarImage(doc: CalendarDocument, options: ShareOptions): Promise<ShareFile> {
  await loadFonts();

  return { name: `${calendarFileName(doc)}.png`, blob: await canvasBlob(draw(doc, options), 'image/png') };
}

/** O calendário numa página A4 deitada: a grade reduzida para caber, centralizada. */
export async function renderCalendarPdf(
  doc: CalendarDocument,
  options: ShareOptions
): Promise<{ pdf: ShareFile; pages: Blob[] }> {
  await loadFonts();
  const source = draw(doc, options);
  const pageW = WIDTH;
  const pageH = Math.round((WIDTH * A4_LANDSCAPE.height) / A4_LANDSCAPE.width);
  const scale = 2;
  const { canvas, ctx } = createCanvas(pageW, pageH, scale);
  const sourceH = source.height / (source.width / WIDTH);
  const fit = Math.min(1, pageH / sourceH);
  const w = WIDTH * fit;
  const h = sourceH * fit;

  ctx.fillStyle = COLORS.page;
  ctx.fillRect(0, 0, pageW, pageH);
  ctx.drawImage(source, (pageW - w) / 2, (pageH - h) / 2, w, h);

  const jpeg = await canvasBlob(canvas, 'image/jpeg', 0.92);
  const bytes = buildPdf(
    [{ jpeg: new Uint8Array(await jpeg.arrayBuffer()), width: pageW * scale, height: pageH * scale }],
    doc.title,
    A4_LANDSCAPE
  );

  return {
    pdf: { name: `${calendarFileName(doc)}.pdf`, blob: new Blob([bytes], { type: 'application/pdf' }) },
    pages: [jpeg],
  };
}
